import { supabase } from '../config/supabase';

const supabaseService = {
  // ==================== BOOKINGS ====================

  async getBookings({ status, search } = {}) {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        booking_treatments (
          id,
          treatment_name,
          duration_minutes,
          price
        )
      `)
      .order('created_at', { ascending: false });

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    if (search) {
      query = query.or(
        `name.ilike.%${search}%,email.ilike.%${search}%`
      );
    }

    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return data;
  },

  async getTodaysBookings() {
    // Get today's date in NZ timezone
    const today = new Date().toLocaleDateString('en-NZ', {
      timeZone: 'Pacific/Auckland',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).split('/').reverse().join('-'); // Convert DD/MM/YYYY to YYYY-MM-DD

    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        booking_treatments (
          id,
          treatment_name,
          duration_minutes,
          price
        )
      `)
      .eq('date', today)
      .eq('status', 'approved')
      .not('time', 'is', null)
      .order('time', { ascending: true });

    if (error) throw new Error(error.message);
    return data;
  },

  async createBooking({ name, email, phone, date, time_range_start, time_range_end, notes, treatments, marketing_consent }) {
    let totalDuration = 0;
    let totalPrice = 0;
    const treatmentNames = [];

    treatments.forEach(t => {
      if (t.duration_minutes) totalDuration += t.duration_minutes;
      const priceNum = parseFloat(t.price.replace(/[^0-9.]/g, ''));
      if (!isNaN(priceNum)) totalPrice += priceNum;
      treatmentNames.push(t.name);
    });

    const totalPriceStr = totalPrice > 0 ? `$${totalPrice}` : 'POA';
    const bookingId = crypto.randomUUID();

    const { error: bookingError } = await supabase
      .from('bookings')
      .insert({
        id: bookingId,
        name,
        email,
        phone,
        date,
        time_range_start,
        time_range_end,
        time: null,
        notes,
        appointment_type: 'new',
        reminder_preference: 'email',
        total_duration: totalDuration || null,
        total_price: totalPriceStr,
        status: 'pending',
        marketing_consent: marketing_consent || false
      });

    if (bookingError) throw new Error(bookingError.message);

    const bookingTreatments = treatments.map(t => ({
      booking_id: bookingId,
      treatment_id: t.id,
      treatment_name: t.name,
      duration_minutes: t.duration_minutes,
      price: t.price
    }));

    const { error: treatmentsError } = await supabase
      .from('booking_treatments')
      .insert(bookingTreatments);

    if (treatmentsError) throw new Error(treatmentsError.message);

    return { id: bookingId };
  },

  async updateBookingStatus(id, status, { finalTime, clientType, rescheduleMessage, calendarEventId } = {}) {
    const updates = { status };

    if (status === 'approved' && finalTime) {
      updates.time = finalTime;
    }
    if (clientType) {
      updates.client_type = clientType;
    }
    if (status === 'rescheduled' && rescheduleMessage) {
      updates.reschedule_message = rescheduleMessage;
    }
    if (calendarEventId) {
      updates.calendar_event_id = calendarEventId;
    }

    const { data, error } = await supabase
      .from('bookings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  // ==================== AVAILABILITY ====================

  // Goes through the edge function rather than the RPC directly, because slots
  // also have to clear Viktoria's Google Calendar — anything personal she's put
  // in her own diary has to block a slot here.
  async getAvailableSlots({ startDate, endDate, durationMinutes, ignoreNotice = false, excludeBookingId = null, restMinutes = 0 }) {
    const { data, error } = await supabase.functions.invoke('availability', {
      body: { startDate, endDate, durationMinutes, ignoreNotice, excludeBookingId, restMinutes },
    });

    if (error) {
      // The function fails closed on a calendar outage. Say so plainly rather
      // than showing an empty week, which reads as "she's fully booked".
      const status = error.context?.status;
      if (status === 503) {
        throw new Error("Availability can't be checked right now. Please try again in a few minutes.");
      }
      // Only reachable when ignoreNotice was requested. "Try again" is actively
      // misleading here — retrying cannot fix a permissions problem, and the
      // likely causes (expired session, an admin who was never promoted) need
      // someone to actually do something.
      if (status === 403) {
        throw new Error('Your admin session has expired or lacks permission. Sign out and back in.');
      }
      throw new Error('Could not load available times. Please try again.');
    }

    if (data?.error) {
      throw new Error(data.message || 'Could not load available times.');
    }

    // { slots, lastBookableDate } rather than a bare array: past the booking
    // window the slot list is empty for a reason the caller cannot otherwise
    // see, and rendering that as "nothing free" is what had clients believing
    // Viktoria was fully booked months out. lastBookableDate is null if the
    // setting could not be read.
    return {
      slots: data?.slots ?? [],
      lastBookableDate: data?.lastBookableDate ?? null,
      // How many otherwise-bookable times her Google Calendar removed. The
      // endpoint has always returned this and nothing ever displayed it, which
      // is part of why "the site says I'm booked when I'm not" was so hard to
      // pin down: the one number that says how much the calendar is taking was
      // computed on every request and thrown away.
      filtered: data?.filtered ?? 0,
    };
  },

  // What is actually in Viktoria's Google Calendar over a span of dates, rather
  // than what is left of it. Same edge function as getAvailableSlots, asked a
  // different question — so it sees the same diary the booking engine sees, and
  // the Schedule tab cannot disagree with the booking form about whether a day
  // is blocked.
  //
  // Admin only. Returns [{ start, end, summary, allDay }] where start and end
  // are ISO strings carrying an explicit NZ offset, and `end` is EXCLUSIVE for
  // an all-day event (a block on the 4th ends at the 5th, 00:00).
  //
  // Never returns an empty list to paper over a failure. On this screen "no
  // Google blocks" and "we could not ask Google" look identical, and the whole
  // point of showing these is that a blocked day must not read as a free one —
  // so a failure is thrown for the caller to say out loud.
  async getBusyPeriods({ startDate, endDate }) {
    const { data, error } = await supabase.functions.invoke('availability', {
      body: { mode: 'busy', startDate, endDate },
    });

    if (error) {
      const status = error.context?.status;
      if (status === 503) {
        throw new Error(
          "Your Google Calendar can't be reached right now, so anything you have blocked out there is missing from this list. Try again in a few minutes."
        );
      }
      // Retrying cannot fix a permissions problem — the likely causes (an
      // expired session, an account that was never made an admin) need someone
      // to actually do something.
      if (status === 403) {
        throw new Error(
          'Your admin session has expired or lacks permission, so your Google Calendar blocks are missing from this list. Sign out and back in.'
        );
      }
      throw new Error(
        'Could not check your Google Calendar, so anything you have blocked out there is missing from this list.'
      );
    }

    // Same failure delivered as a 200 with an error body.
    if (data?.error) {
      if (data.error === 'calendar_unavailable') {
        throw new Error(
          "Your Google Calendar can't be reached right now, so anything you have blocked out there is missing from this list. Try again in a few minutes."
        );
      }
      throw new Error(
        data.message ||
          'Could not check your Google Calendar, so anything you have blocked out there is missing from this list.'
      );
    }

    return data?.busy ?? [];
  },

  // ==================== INSTANT BOOKING ====================

  // Only treatment ids are sent. Duration and price are derived server-side —
  // the browser must not be able to assert what an appointment costs.
  async createInstantBooking({ name, email, phone, date, time, treatmentIds, notes, marketingConsent }) {
    const { data, error } = await supabase.rpc('create_booking', {
      p_name: name,
      p_email: email,
      p_phone: phone,
      p_date: date,
      p_time: time,
      p_treatment_ids: treatmentIds,
      p_notes: notes || null,
      p_marketing_consent: !!marketingConsent,
    });

    if (error) {
      // 23505 is raised when the slot went while the client was filling the form.
      if (error.code === '23505' || /just been taken/i.test(error.message)) {
        const taken = new Error('That time has just been taken. Please choose another.');
        taken.slotTaken = true;
        throw taken;
      }
      throw new Error(error.message || 'Could not complete your booking.');
    }

    return { id: data };
  },

  // ==================== EMAIL FAILURES ====================
  // With nobody approving bookings any more, a dead email provider is completely
  // silent — the last outage ran for months unnoticed. These back the banner
  // that makes it visible.

  async getEmailFailures() {
    const { data, error } = await supabase
      .from('email_failures')
      .select('*')
      .eq('acknowledged', false)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error) throw new Error(error.message);
    return data || [];
  },

  async acknowledgeEmailFailures() {
    const { error } = await supabase
      .from('email_failures')
      .update({ acknowledged: true })
      .eq('acknowledged', false);
    if (error) throw new Error(error.message);
  },

  // ==================== RESCHEDULING ====================

  // She moves a confirmed booking to a new time. It moves immediately -- there
  // is no proposal, no limbo state and nothing for the client to accept. The
  // email tells them, and carries a link to pick something else if it does not
  // suit.
  async moveBooking(bookingId, { date, time, note }) {
    const { data, error } = await supabase.rpc('move_booking', {
      p_booking_id: bookingId,
      p_new_date: date,
      p_new_time: time,
      p_note: note || null,
    });

    if (error) {
      if (error.code === '23505' || /not free/i.test(error.message)) {
        const taken = new Error('That time has just been taken. Please choose another.');
        taken.slotTaken = true;
        throw taken;
      }
      throw new Error(error.message);
    }

    let notified = false;
    let calendarEventId = null;

    try {
      // The move is already committed. Losing the email or the calendar entry is
      // worth reporting loudly, never worth failing the move over -- and the
      // reconciliation sweep will finish the job if this never runs.
      const result = await supabaseService.sendNotification('booking_moved', {
        id: bookingId,
        old_date: data?.old_date || null,
        old_time: data?.old_time || null,
        old_calendar_event_id: data?.old_calendar_event_id || null,
      });
      notified = !!result?.emailResults?.[0]?.ok;
      calendarEventId = result?.calendarEventId || null;
    } catch (err) {
      console.error('Move notification failed:', err);
    }

    return { ...data, notified, calendarEventId };
  },

  // ==================== AVAILABILITY MANAGEMENT ====================
  // Everything that decides when clients can book. Admin only — the policies on
  // these tables all check is_admin().

  async getLocations() {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .order('name');
    if (error) throw new Error(error.message);
    return data || [];
  },

  // Always returns all seven days, including closed ones, so the editor can show
  // a complete week rather than only the days that happen to have a row.
  async getBusinessHours() {
    const { data, error } = await supabase
      .from('business_hours')
      .select('*, locations(name, address)')
      .order('weekday');
    if (error) throw new Error(error.message);
    return data || [];
  },

  async updateBusinessHours(weekday, { is_open, open_time, close_time, location_id }) {
    const { data, error } = await supabase
      .from('business_hours')
      .update({
        // Times are kept when a day is closed, not nulled. business_hours_times_valid
        // only constrains OPEN days, and get_available_slots excludes a closed day
        // on is_open alone — so the stored times are inert while closed and are the
        // only record of what the day used to be. Nulling them meant closing Mondays
        // for the winter silently lost 12:00–19:30, and the editor's 09:00–17:00
        // placeholder then looked like saved data on reopening.
        is_open,
        open_time: open_time || null,
        close_time: close_time || null,
        location_id: location_id || null,
      })
      .eq('weekday', weekday)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  // Upcoming only. Past exceptions are history nobody needs to scroll through.
  async getScheduleExceptions() {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const { data, error } = await supabase
      .from('schedule_exceptions')
      .select('*, locations(name)')
      .gte('exception_date', iso)
      .order('exception_date');
    if (error) throw new Error(error.message);
    return data || [];
  },

  // The same rows, but for a stated span of dates instead of "from today on".
  // The Availability panel only ever edits the future, so getScheduleExceptions
  // is right to hide the past; the Schedule tab has a month picker and has to be
  // able to say why a day that has already gone was empty.
  async getScheduleExceptionsInRange(startDate, endDate) {
    const { data, error } = await supabase
      .from('schedule_exceptions')
      .select('*, locations(name)')
      .gte('exception_date', startDate)
      .lte('exception_date', endDate)
      .order('exception_date');
    if (error) throw new Error(error.message);
    return data || [];
  },

  async saveScheduleException({ exception_date, is_closed, open_time, close_time, location_id, note }) {
    const { data, error } = await supabase
      .from('schedule_exceptions')
      .upsert({
        exception_date,
        is_closed,
        open_time: is_closed ? null : open_time,
        close_time: is_closed ? null : close_time,
        location_id: is_closed ? null : (location_id || null),
        note: note || null,
      }, { onConflict: 'exception_date' })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  // .select() so a delete that matched nothing is an error rather than a silent
  // success. This screen's entire job is answering "did that take effect".
  async deleteScheduleException(id) {
    const { data, error } = await supabase
      .from('schedule_exceptions').delete().eq('id', id).select('id');
    if (error) throw new Error(error.message);
    if (!data?.length) throw new Error('That change was already removed. Refreshing the list.');
  },

  // start_at / end_at are NZ wall-clock timestamps, matching business_hours and
  // bookings. Send 'YYYY-MM-DDTHH:MM' — no timezone suffix, or it gets shifted.
  async getTimeOff() {
    // Local parts, not toISOString() — in NZ that returns yesterday's date for
    // the first half of the working day, which is the exact trap this codebase
    // has already been caught by twice.
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const { data, error } = await supabase
      .from('time_off')
      .select('*')
      .gte('end_at', today)
      .order('start_at');
    if (error) throw new Error(error.message);
    return data || [];
  },

  // Every block that touches the span, including one that started before it and
  // one that finishes after it — a week's holiday has to still show up on the
  // Schedule tab on the Wednesday in the middle of it.
  //
  // The bounds are plain wall-clock text for the same reason the writes are:
  // these columns carry no timezone, so a bound with a Z or an offset on it
  // would be read 12 or 13 hours away from the day she is looking at.
  async getTimeOffInRange(startDate, endDate) {
    const { data, error } = await supabase
      .from('time_off')
      .select('*')
      .lte('start_at', `${endDate}T23:59:59`)
      .gte('end_at', `${startDate}T00:00:00`)
      .order('start_at');
    if (error) throw new Error(error.message);
    return data || [];
  },

  async addTimeOff({ start_at, end_at, reason }) {
    const { data, error } = await supabase
      .from('time_off')
      .insert({ start_at, end_at, reason: reason || null })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async deleteTimeOff(id) {
    const { data, error } = await supabase
      .from('time_off').delete().eq('id', id).select('id');
    if (error) throw new Error(error.message);
    if (!data?.length) throw new Error('That block was already removed. Refreshing the list.');
  },

  async getBookingSettings() {
    const { data, error } = await supabase
      .from('booking_settings')
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },

  async updateBookingSettings(updates) {
    const { data, error } = await supabase
      .from('booking_settings')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', true)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  // Which confirmed appointments a proposed block would sit on top of. Blocking
  // time she is already booked for is the one thing here that can strand a
  // client, so it must be shown before she commits, not after.
  async getBookingsInRange(startDate, endDate) {
    const { data, error } = await supabase
      .from('bookings')
      .select('id, name, status, date, time, total_duration, booking_treatments(treatment_name)')
      // Includes 'rescheduled': those clients have not been told not to come, so
      // reporting "nothing is booked" over one is a false all-clear on the one
      // screen where that costs someone a wasted trip.
      .in('status', ['approved', 'rescheduled'])
      .gte('date', startDate)
      .lte('date', endDate)
      .not('time', 'is', null)
      .order('date')
      .order('time');
    if (error) throw new Error(error.message);
    return data || [];
  },

  // ==================== MY ACCOUNT ====================
  // Everything a signed-in client can see or do about themselves. All of it is
  // scoped by auth.uid() inside the database, never by a filter passed from here.

  async signUp({ email, password, fullName, phone }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName, phone } },
    });
    if (error) throw new Error(error.message);
    return data;
  },

  async signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error('That email and password combination did not work.');
    return data;
  },

  // Redirects the whole browser to Google, so nothing after the call runs on the
  // happy path. The client comes back to /account with the session in the URL,
  // which supabase-js picks up and turns into a SIGNED_IN event.
  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/account` },
    });
    if (error) throw new Error(error.message);
    return data;
  },

  async signOut() {
    await supabase.auth.signOut();
  },

  async sendPasswordReset(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/account`,
    });
    if (error) throw new Error(error.message);
  },

  async getMyProfile() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },

  // role, and the birthday once set, are protected by a database trigger — they
  // are silently ignored here rather than rejected.
  async updateMyProfile({ full_name, phone, birth_day, birth_month }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not signed in.');

    const updates = { full_name, phone };
    if (birth_day && birth_month) {
      updates.birth_day = Number(birth_day);
      updates.birth_month = Number(birth_month);
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw new Error(error.message);

    // The header reads the name from auth metadata rather than querying profiles
    // on every page load, so it has to be kept in step — otherwise renaming
    // yourself updates the account page and leaves the old name in the header
    // indefinitely.
    if (full_name && full_name !== user.user_metadata?.full_name) {
      const { error: metaError } = await supabase.auth.updateUser({
        data: { full_name, phone },
      });
      if (metaError) console.error('Could not update auth metadata:', metaError);
    }

    return data;
  },

  // What they last had, so a returning client only has to choose when. Returns
  // null if they have no completed visit, or if none of its treatments are still
  // bookable.
  async getMyLastVisit() {
    const { data, error } = await supabase.rpc('get_my_last_visit');
    if (error) throw new Error(error.message);
    return data || null;
  },

  async getMyBookings() {
    const { data, error } = await supabase.rpc('get_my_bookings');
    if (error) throw new Error(error.message);
    return data || [];
  },

  // Cancelling is two steps because Postgres cannot reach Google. The RPC frees
  // the slot; the edge function removes the event from Viktoria's calendar and
  // tells her. Sends only the booking id — the function loads the rest itself and
  // checks the caller owns it, so nobody can cancel or un-calendar someone else's
  // appointment by passing a different payload.
  async cancelMyBooking(bookingId) {
    const { error } = await supabase.rpc('cancel_my_booking', { p_booking_id: bookingId });
    if (error) throw new Error(error.message);

    // The booking is already cancelled and the slot already free in Postgres. If
    // this half fails the client is not made to care -- but Viktoria's calendar
    // is then stale, and since availability reads every event in it as busy, the
    // slot she just got back is blocked again with nothing to say why.
    //
    // The old comment here claimed the reconciliation sweep picked this up. It
    // did not: booking-reconcile filters status='approved' and only ever creates
    // events, so a cancelled booking was never in its scope and no sweep in the
    // system ran in this direction at all. Returning the outcome so the caller
    // can decide what to do, and saying plainly in the log what was left behind.
    //
    // sendNotification swallows a failed invoke and resolves to null, so a null
    // here means the call never landed -- the event is certainly still there.
    try {
      const result = await supabaseService.sendNotification('client_cancelled', { id: bookingId });
      if (!result) {
        console.error(
          `Cancellation for booking ${bookingId} did not reach the notification function. ` +
            'Viktoria has not been told and her calendar event is still in place.'
        );
        return { calendarDeleted: false, notified: false };
      }
      if (result.calendarDeleted === false) {
        console.error(
          `Booking ${bookingId} was cancelled but its Google Calendar event could not be removed. ` +
            'That time stays blocked until the event is deleted by hand.'
        );
      }
      return { calendarDeleted: result.calendarDeleted !== false, notified: true };
    } catch (err) {
      console.error('Cancellation notification failed:', err);
      return { calendarDeleted: false, notified: false };
    }
  },

  // ==================== CLIENTS ====================

  // Clients are identified by email, not user id — most have never made an
  // account, and a visit history that only covered registered clients would be
  // useless to Viktoria.
  async searchClients(query) {
    const { data, error } = await supabase.rpc('search_clients', {
      p_query: query || null,
    });
    if (error) throw new Error(error.message);
    return data || [];
  },

  // Every visit with its treatments and products in one call.
  async getClientHistory(email) {
    const { data, error } = await supabase.rpc('get_client_history', {
      p_email: email,
    });
    if (error) throw new Error(error.message);
    return data || [];
  },

  // Whether this client has asked not to receive the annual birthday offer.
  //
  // Read straight from profiles rather than folded into search_clients: only
  // clients with an account have the flag at all, and the Clients tab is mostly
  // guests. Admins can read and update any profile under RLS, and
  // protect_profile_fields() deliberately leaves this column unpinned, so an
  // opt-out is one of the few profile fields she is allowed to set for someone
  // else. Covers the birthday email only -- reminders keep sending.
  async getBirthdayOptOut(userId) {
    if (!userId) return false;
    const { data, error } = await supabase
      .from('profiles')
      .select('birthday_emails_opt_out')
      .eq('id', userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return Boolean(data?.birthday_emails_opt_out);
  },

  async setBirthdayOptOut(userId, optOut) {
    if (!userId) throw new Error('That client does not have an account yet.');
    // Read the value back rather than trusting a 204: a trigger could revert it
    // silently, which has happened on this table before.
    const { data, error } = await supabase
      .from('profiles')
      .update({ birthday_emails_opt_out: Boolean(optOut) })
      .eq('id', userId)
      .select('birthday_emails_opt_out')
      .single();
    if (error) throw new Error(error.message);
    if (Boolean(data?.birthday_emails_opt_out) !== Boolean(optOut)) {
      throw new Error('That did not save. Please try again.');
    }
    return Boolean(data.birthday_emails_opt_out);
  },

  // ==================== WHAT A CLIENT ACTUALLY RECEIVED ====================
  // Booking totals and stock are maintained by database triggers, so these only
  // insert and delete rows. Do not recalculate anything here.

  async addBookingTreatment(bookingId, treatment) {
    const { data, error } = await supabase
      .from('booking_treatments')
      .insert({
        booking_id: bookingId,
        treatment_id: treatment.id || null,
        treatment_name: treatment.name,
        duration_minutes: treatment.duration_minutes,
        price: treatment.price,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async removeBookingTreatment(id) {
    const { error } = await supabase.from('booking_treatments').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  async addBookingProduct(bookingId, product, quantity = 1) {
    const cents = Math.round(
      parseFloat(String(product.price).replace(/[^0-9.]/g, '') || '0') * 100
    );
    const { data, error } = await supabase
      .from('booking_products')
      .insert({
        booking_id: bookingId,
        product_id: product.id || null,
        product_name: product.name,
        price: product.price,
        price_cents: isNaN(cents) ? 0 : cents,
        quantity,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async removeBookingProduct(id) {
    const { error } = await supabase.from('booking_products').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ==================== ADMIN REBOOK ====================

  // Slots for Viktoria, who is allowed inside the minimum-notice window — she may
  // be booking someone in for tomorrow morning while they stand in front of her.
  //
  // Goes through the same edge function as the client path, not the RPC directly.
  // Calling the RPC skipped the Google Calendar check, so a rebook could be
  // offered on top of a personal appointment she'd put in her own diary — the
  // one case where nobody is watching for the clash.
  // excludeBookingId is passed when moving a booking, so it does not count
  // itself as busy and block the fifteen-minute nudge.
  // Removes one entry from Viktoria's Google Calendar by its raw event id, for
  // the import: once a booking has replaced the line she typed in herself, the
  // original has to go or it keeps blocking the slot the booking now occupies.
  //
  // Never call this to get rid of a booking. The edge function refuses an event
  // attached to a live booking (409) precisely because doing it this way would
  // take the appointment out of the only place she looks while the dashboard
  // went on showing it. Cancel or move the booking instead.
  async deleteCalendarEventById(eventId) {
    // Invoked directly rather than through sendNotification, which turns every
    // non-2xx into a null and would report the guard below as "could not reach
    // the calendar" — the one message that is definitely wrong, because the
    // function answered and answered deliberately.
    const { data, error } = await supabase.functions.invoke('booking-notification', {
      body: { action: 'delete_calendar_event', booking: { calendar_event_id: eventId } },
    });

    if (error) {
      const status = error.context?.status;
      if (status === 409) {
        throw new Error(
          'That calendar entry belongs to a booking already in here. Cancel or move the booking instead.'
        );
      }
      if (status === 403) {
        throw new Error('Your admin session has expired. Sign out and back in.');
      }
      throw new Error('Could not reach the calendar. The old entry is still there.');
    }

    if (data?.error) throw new Error(data.error);
    return data?.calendarDeleted === true;
  },

  async getAdminSlots({ startDate, endDate, durationMinutes, excludeBookingId = null, restMinutes = 0 }) {
    // Unwrapped to a plain array. Her own pickers never hit the advance-booking
    // wall -- ignoreNotice admins book inside it -- so the bound would be noise
    // here, and unwrapping keeps every existing caller working unchanged.
    const { slots } = await supabaseService.getAvailableSlots({
      startDate,
      endDate,
      durationMinutes,
      ignoreNotice: true,
      excludeBookingId,
      restMinutes,
    });
    return slots;
  },

  // booking_source records how the booking was taken: 'admin_rebook' for an
  // in-person rebook, 'admin_manual' for one taken by phone or text. Shown back
  // to Viktoria on the client's visit history.
  //
  // Sends the confirmation and writes the calendar event itself. A rebook that
  // silently skipped those would leave the client with no confirmation and the
  // appointment missing from Viktoria's diary — which is the one place she looks.
  async rebookClient({
    name, email, phone, date, time, treatmentIds, notes,
    source = 'admin_rebook',
    treatmentNames, totalDuration, totalPrice, clientType,
  }) {
    const { data, error } = await supabase.rpc('create_booking', {
      p_name: name,
      p_email: email,
      p_phone: phone,
      p_date: date,
      p_time: time,
      p_treatment_ids: treatmentIds,
      p_notes: notes || null,
      p_marketing_consent: false,
      p_source: source,
    });

    if (error) {
      if (error.code === '23505' || /just been taken/i.test(error.message)) {
        const taken = new Error('That time has just been taken. Please choose another.');
        taken.slotTaken = true;
        throw taken;
      }
      throw new Error(error.message);
    }

    const bookingId = data;
    let notified = false;
    let emailSkipped = false;
    let calendarEventId = null;

    try {
      // The booking already carries the resolved address; passing it stops
      // rebookConfirmationEmail falling back to the hardcoded weekday rule.
      const { data: created } = await supabase
        .from('bookings')
        .select('location_address, client_type')
        .eq('id', bookingId)
        .maybeSingle();

      const result = await supabaseService.sendNotification('rebook_confirmed', {
        id: bookingId,
        location_address: created?.location_address || null,
        name, email, phone, date, time,
        notes: notes || null,
        treatmentNames: treatmentNames || '',
        total_duration: totalDuration || null,
        total_price: totalPrice || null,
        clientType: created?.client_type || clientType || 'returning',
      });

      // `skipped` means there was no address to send to — a walk-in Viktoria
      // booked in person. sendEmail reports that as ok:true so it never reaches
      // her failure banner, which means `notified` alone would claim the client
      // had been emailed when nobody was. Kept separate so the toast can say
      // what actually happened.
      emailSkipped = !!result?.emailResults?.[0]?.skipped;
      notified = !emailSkipped && !!result?.emailResults?.[0]?.ok;
      calendarEventId = result?.calendarEventId || null;
      // The edge function persists the calendar id itself via
      // mark_confirmation_sent — writing it back from here as well raced with
      // that and could report success while the id was never stored.
    } catch (err) {
      // The booking is already real. Losing the email or the calendar event is
      // worth reporting, never worth failing the booking over.
      console.error('Rebook confirmation failed:', err);
    }

    return { id: bookingId, notified, emailSkipped, calendarEventId };
  },

  // ==================== TREATMENTS ====================

  async getTreatments() {
    const { data, error } = await supabase
      .from('treatments')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw new Error(error.message);

    const grouped = {};
    data.forEach(t => {
      if (!grouped[t.category]) {
        grouped[t.category] = [];
      }
      grouped[t.category].push(t);
    });

    return { all: data, grouped };
  },

  // ==================== REBOOKING ====================

  async getRebookingData(bookingId) {
    const { data, error } = await supabase.rpc('get_rebooking', {
      booking_uuid: bookingId
    });

    if (error) throw new Error(error.message);
    return data;
  },

  async submitRebooking(bookingId, { date, time_range_start, time_range_end, notes }) {
    const { data, error } = await supabase.rpc('submit_rebooking', {
      booking_uuid: bookingId,
      new_date: date,
      new_time_range_start: time_range_start,
      new_time_range_end: time_range_end,
      new_notes: notes || null
    });

    if (error) throw new Error(error.message);
    return data;
  },

  // ==================== NOTIFICATIONS ====================

  async sendNotification(action, bookingData) {
    try {
      console.log('Calling Edge Function: booking-notification, action:', action);

      const { data, error } = await supabase.functions.invoke('booking-notification', {
        body: { action, booking: bookingData },
      });

      if (error) {
        console.error('Edge Function error:', error);
        return null;
      }

      console.log('Edge Function response:', data);
      return data;
    } catch (err) {
      console.error('Failed to send notification:', err);
      return null;
    }
  },

  // ==================== SHOP PRODUCTS ====================

  async getShopProducts() {
    const { data, error } = await supabase
      .from('shop_products')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });
    if (error) throw new Error(error.message);
    return data;
  },

  async getAllShopProducts() {
    const { data, error } = await supabase
      .from('shop_products')
      .select('*')
      .order('display_order', { ascending: true });
    if (error) throw new Error(error.message);
    return data;
  },

  async createShopProduct({ name, description, price, category, image_url, is_active, display_order }) {
    const { data, error } = await supabase
      .from('shop_products')
      .insert({ name, description, price, category, image_url, is_active: is_active ?? true, display_order: display_order ?? 0 })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async updateShopProduct(id, updates) {
    const { data, error } = await supabase
      .from('shop_products')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async deleteShopProduct(id) {
    const { error } = await supabase
      .from('shop_products')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ==================== STOCK ====================

  async updateProductStock(id, stock) {
    // stock = null means untracked, any number means tracked
    const { data, error } = await supabase
      .from('shop_products')
      .update({ stock: stock === '' || stock === null ? null : Number(stock) })
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async deductStockForOrder(orderItems) {
    // orderItems: array of { product_id, quantity } from shop_order_items
    for (const item of orderItems) {
      if (!item.product_id) continue;
      const { data: product, error } = await supabase
        .from('shop_products')
        .select('stock')
        .eq('id', item.product_id)
        .single();
      if (error || product == null || product.stock === null) continue; // null = untracked, skip
      // Not clamped at zero: the booking_products trigger lets stock go negative
      // so that deduct and restore stay exact inverses, and these two paths must
      // agree about what an oversold product looks like.
      const newStock = product.stock - item.quantity;
      const { error: updateErr } = await supabase
        .from('shop_products')
        .update({ stock: newStock })
        .eq('id', item.product_id);
      if (updateErr) console.error('Stock deduct failed for', item.product_id, updateErr);
    }
  },

  // ==================== SHOP ORDERS ====================

  async getShopOrders() {
    const { data, error } = await supabase
      .from('shop_orders')
      .select(`*, shop_order_items(*)`)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data;
  },

  async createShopOrder({ customer_name, email, fulfillment, address, notes, items, marketing_consent }) {
    const orderId = crypto.randomUUID();
    const { error: orderError } = await supabase
      .from('shop_orders')
      .insert({ id: orderId, customer_name, email, fulfillment, address, notes, marketing_consent: marketing_consent || false });
    if (orderError) throw new Error(orderError.message);

    const orderItems = items.map(item => ({
      order_id: orderId,
      product_id: item.id || null,
      product_name: item.name,
      price: item.price,
      quantity: item.quantity,
    }));

    const { error: itemsError } = await supabase
      .from('shop_order_items')
      .insert(orderItems);
    if (itemsError) throw new Error(itemsError.message);

    return { id: orderId };
  },

  async updateShopOrderStatus(id, status, responseMessage) {
    const updates = { status };
    if (responseMessage) updates.response_message = responseMessage;
    const { data, error } = await supabase
      .from('shop_orders')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async deleteShopOrder(id) {
    await supabase.from('shop_order_items').delete().eq('order_id', id);
    const { error } = await supabase.from('shop_orders').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ==================== GIFT VOUCHERS ====================

  async createGiftVoucher({ purchaser_name, purchaser_email, purchaser_phone, recipient_name, amount, delivery_method, pickup_date, notes }) {
    const { error } = await supabase.from('gift_vouchers').insert({
      purchaser_name, purchaser_email, purchaser_phone,
      recipient_name: recipient_name || null,
      amount: parseFloat(amount),
      delivery_method,
      pickup_date: pickup_date || null,
      notes: notes || null,
    });
    if (error) throw error;
  },

  async getGiftVouchers() {
    const { data, error } = await supabase
      .from('gift_vouchers')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async updateGiftVoucherStatus(id, status) {
    const { error } = await supabase
      .from('gift_vouchers')
      .update({ status })
      .eq('id', id);
    if (error) throw error;
  },

  // "Payment received" — stamps a code, an issue date and a three-year expiry.
  // Idempotent in the database: pressing it twice returns the same voucher with
  // already_issued: true rather than minting a second code.
  async issueGiftVoucher(id) {
    const { data, error } = await supabase.rpc('issue_gift_voucher', { p_id: id });
    if (error) throw new Error(error.message);
    return data;
  },

  // "Gift voucher used". The RPC's error messages are written for Viktoria to
  // read ("That voucher only has $115.00 left on it"), so pass them through
  // untouched rather than flattening them into a generic failure.
  //
  // amountCents is null for "spend whatever is left", which is what happens
  // most of the time and is deliberately the default — she should not have to
  // type a number to use a voucher up. A number spends exactly that much and
  // leaves the rest live. Cents, always: the balance is integer cents in the
  // database and dollars never make the trip.
  //
  // Returns { id, code, amount, spent_cents, balance_cents, fully_used, status,
  // purchaser_name, recipient_name, expires_at, was_expired }.
  async redeemGiftVoucher(code, amountCents = null, note = null) {
    const { data, error } = await supabase.rpc('redeem_gift_voucher', {
      p_code: code,
      p_amount_cents: Number.isFinite(amountCents) ? amountCents : null,
      p_note: note || null,
    });
    if (error) throw new Error(error.message);
    return data;
  },

  // Public: the printable voucher page. Goes through a security definer lookup
  // rather than a table select — the row holds the purchaser's email, phone and
  // private note, and a voucher gets forwarded to strangers by design.
  // Returns null for an unknown code; that is a state to render, not an error.
  async getGiftVoucherByCode(code) {
    const { data, error } = await supabase.rpc('get_gift_voucher', { p_code: code });
    if (error) throw new Error(error.message);
    return data;
  },

  // ==================== PROMOTIONS ====================

  // type = 'banner' | 'section'
  async getActivePromotion(type = 'banner') {
    const { data, error } = await supabase
      .from('promotions')
      .select('*')
      .eq('is_active', true)
      .eq('type', type)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },

  async getAllPromotions() {
    const { data, error } = await supabase
      .from('promotions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data;
  },

  async createPromotion({ title, message, is_active, bg_color, type, image_url }) {
    const { data, error } = await supabase
      .from('promotions')
      .insert({ title, message, is_active: is_active ?? false, bg_color: bg_color || '#2a4e3a', type: type || 'banner', image_url: image_url || null })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async updatePromotion(id, updates) {
    const { data, error } = await supabase
      .from('promotions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async deletePromotion(id) {
    const { error } = await supabase
      .from('promotions')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ==================== REALTIME ====================

  subscribeToBookings(callback) {
    const channel = supabase
      .channel('bookings-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bookings' },
        (payload) => callback(payload)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  // ==================== EMAIL BLAST ====================

  // Consent is filtered here, not at the send. It has been collected on every
  // form since the beginning and never once used — under the NZ Unsolicited
  // Electronic Messages Act that is the difference between a mailing list and a
  // compliance problem.
  async getUniqueCustomerEmails(audience = 'all') {
    const emails = new Map(); // email -> name

    if (audience === 'all' || audience === 'bookings') {
      const { data: bookings } = await supabase
        .from('bookings')
        .select('name, email')
        .eq('marketing_consent', true)
        .not('email', 'is', null);
      (bookings || []).forEach(b => { if (b.email) emails.set(b.email.toLowerCase(), b.name); });
    }

    if (audience === 'all' || audience === 'orders') {
      const { data: orders } = await supabase
        .from('shop_orders')
        .select('customer_name, email')
        .eq('marketing_consent', true)
        .not('email', 'is', null);
      (orders || []).forEach(o => { if (o.email) emails.set(o.email.toLowerCase(), o.customer_name); });
    }

    return Array.from(emails.entries()).map(([email, name]) => ({ email, name }));
  },

  async sendEmailBlast(subject, html, recipients) {
    try {
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/email-blast`;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('You need to be signed in to send a blast.');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // The session token, not the anon key — the function checks this
          // caller is an admin, and the anon key identifies nobody.
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ subject, html, recipients }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send');
      return data;
    } catch (err) {
      console.error('Email blast failed:', err);
      throw err;
    }
  },

  // ==================== ANALYTICS ====================

  async getBookingsForPeriod(startDate, endDate) {
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        booking_treatments (
          id,
          treatment_name,
          duration_minutes,
          price
        )
      `)
      .eq('status', 'approved')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });
    if (error) throw new Error(error.message);
    return data;
  },

  async getFulfilledOrdersForPeriod(startDate, endDate) {
    const { data, error } = await supabase
      .from('shop_orders')
      .select(`
        *,
        shop_order_items (
          id,
          product_name,
          price,
          quantity
        )
      `)
      .eq('status', 'fulfilled')
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59');
    if (error) throw new Error(error.message);
    return data;
  },

  subscribeToOrders(callback) {
    const channel = supabase
      .channel('orders-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shop_orders' },
        (payload) => callback(payload)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }
};

export default supabaseService;